export const reportMap = {
    researchReport: "In-Depth Analysis",
    shortReport: "Concise Summary",
    companyReport: "Business Blueprint",
    swotReport: "SWOT Analysis",
    pestlReport: "PESTL Analysis",
    newsReport: "News Signals & Target Lists",
    installationAnalysis: "Largest Clean Tech Deployments",
    cancellationAnalysis: "Canceled Clean Tech Projects",
    regulations: "Regulations",
    "m&a": "M&A",
    thesisValidation: "Thesis Validation",
    briefPoints: "Brief Points"
}

export const followUpQuestions = [
    "What is the market size of solid oxide fuel cells?",
    "What are the market drivers for carbon capture and utilization?",
    "How does a fuel cell work?",
    "What partnerships has Shell established about carbon capture?",
    "What are the methane regulations in the US?"
]

export const defaultFollowUpQuestions = {
    researchReport: [
        "What is the current and projected market size for green hydrogen?",
        "What are the key drivers and challenges shaping the carbon capture and utilization market?",
        "What are the latest carbon emissions regulations in the US?",
        "How do fuel cells compare to batteries for grid storage and transportation applications?",
        "What are corporate sustainability commitments of Chevron for 2030?"
    ],
    shortReport: [
        "What is the current and projected market size for solid oxide fuel cells?",
        "What are the key drivers and challenges shaping the carbon capture and utilization market?",
        "How do different types of fuel cells compare in terms of efficiency, cost, and scalability?",
        "What are the latest carbon emissions regulations in the US?",
        "How do fuel cells compare to batteries for grid storage and transportation applications?",
        "What are corporate sustainability commitments of Chevron for 2030?"
    ],
    newsReport: [
        "Who are the 20 leading Solid Oxide Fuel Cell companies in maritime shipping?",
        "Show the 20 fastest-growing BESS startups for the U.S. grid?",
        "What are the top 20 carbon capture news headlines from the past week?",
        "Show the top 10 news headlines about green hydrogen in steel production from the past month.",
        "Who are the top 20 competitors of Walmart?"
    ],
    companyReport: [
        "What does ExxonMobil do in carbon capture and storage?",
        "How is Vattenfall investing in green hydrogen production?",
        "Who are Bloom Energy’s key partners in solid oxide fuel cells?",
        "Is Shell working on direct air capture technology?",
        "What recent collaborations has Siemens Energy made in electrolyzer technology?",
        "What should we watch for from Chevron in the hydrogen market?"
    ],
    swotReport: [
        "Break down Bloom Energy's SOFC playbook across 2021–present.",
        "Analyze ExxonMobil's CCS strategy in the Gulf Coast across two periods.",
        "Show Siemens Energy's electrolyzer manufacturing posture over time.",
        "Compare Ørsted's offshore wind strengths versus threats since 2021."
    ],
    pestlReport: [
        "Assess European green hydrogen developers across political, economic, social, technological, legal shifts.",
        "Map U.S. SAF producers against policy, cost, social, technology, legal signals.",
        "Show how political and legal factors evolved for Brazil's biofuel exports.",
        "Explain the economic and regulatory landscape for lithium refining in Australia."
    ],
    installationAnalysis: [
        "What are the 10 largest solid oxide fuel cell installations worldwide?",
        "Where are the biggest hydrogen projects happening?",
        "Which companies are deploying large-scale carbon capture installations?",
        "What are the 20 largest solar installations by capacity?",
        "Which are the 20 largest wind energy projects worldwide?"
    ],
    cancellationAnalysis: [
        "What are the 10 largest offshore wind projects canceled?",
        "Which hydrogen projects have been canceled or delayed?",
        "Which companies have scrapped large-scale carbon capture installations?",
        "What are the largest canceled renewable energy projects?",
        "What are the 20 largest canceled solar installations by capacity?"
    ],
    regulations: [
        "What are the 10 largest solid oxide fuel cell installations worldwide?",
        "Where are the biggest hydrogen projects happening?",
        "Which companies are deploying large-scale carbon capture installations?",
        "What are the 20 largest solar installations by capacity?",
        "Which are the 20 largest wind energy projects worldwide?"
    ],
    "m&a": [
        "What are the 10 largest solid oxide fuel cell installations worldwide?",
        "Where are the biggest hydrogen projects happening?",
        "Which companies are deploying large-scale carbon capture installations?",
        "What are the 20 largest solar installations by capacity?",
        "Which are the 20 largest wind energy projects worldwide?"
    ],
    thesisValidation: [
        "What are the 10 largest solid oxide fuel cell installations worldwide?",
        "Where are the biggest hydrogen projects happening?",
        "Which companies are deploying large-scale carbon capture installations?",
        "What are the 20 largest solar installations by capacity?",
        "Which are the 20 largest wind energy projects worldwide?"
    ],
    briefPoints: [
        "What is the current and projected market size for solid oxide fuel cells?",
        "What are the key drivers and challenges shaping the carbon capture and utilization market?",
        "How do different types of fuel cells compare in terms of efficiency, cost, and scalability?",
        "What are the latest carbon emissions regulations in the US?",
        "How do fuel cells compare to batteries for grid storage and transportation applications?",
        "What are corporate sustainability commitments of Chevron for 2030?"
    ]
}

export const reportFollowUpQuestions = {
    "companyReport": [
        "Expand on partnerships",
        "Expand on investments",
        "Expand on products and technologies"
    ],
    "swotReport": [
        "Highlight Strengths only",
        "Highlight Weaknesses only",
        "Highlight Opportunities only",
        "Highlight Threats only"
    ],
    "pestlReport": [
        "Dive deeper into Political factors",
        "Dive deeper into Economic factors",
        "Dive deeper into Social factors",
        "Dive deeper into Technological factors",
        "Dive deeper into Legal & Regulatory factors"
    ],
    "installationAnalysis": [
        "Expand on key insights"
    ],
    "cancellationAnalysis": [
        "Expand on key insights"
    ]
}

export const reportDescriptions = {
    researchReport: `Get detailed, data-rich insights that go beyond surface-level answers. Enki provides structured, in-depth responses—<b>up to 1,200 words</b>—complete with trends, context, and strategic takeaways.

🔹 <b>Deep-Dive Research</b>: Explore market dynamics, competitor moves, and industry trends with rich analysis.
🔹 <b>Strategic Perspective</b>: Gain well-structured insights that connect facts to actionable business decisions.
🔹 <b>Top Players & Competitive Landscape</b>: Understand which companies are leading the energy transition and how they are shaping the market.
🔹 <b>Leadership-Ready Reports</b>: Save time with <b>comprehensive, polished</b> content—perfect for internal use or executive briefings.

For when you need depth, not just data. 📊✨
`,
 shortReport: `Get instant, sector-specific insights in under <b>400 words</b>-straight from the latest web, blogs, and top news sources.

Any clean tech like carbon capture, fuel cells,

🔹 Always Up to Date – Stay ahead with the most recent market trends, policies, and deals—no outdated reports.
🔹 Laser-Focused Accuracy – Unlike generic AI, Enki zeroes in on energy transition for relevant, precise insights.
🔹 Faster Research, Smarter Strategy – Skip the hours of searching—get real-time answers tailored to your industry.

Clear. Fast. Actionable. 🚀`,
    newsReport: `<b>Follow the latest trends. Discover the next movers.</b>

📰 Get the latest news and emerging players across the clean tech market.
📆 Filter by last week, month, or quarter to zero in on fresh signals and trends.
🏗️ Build curated company lists in niche segments — from ocean carbon capture to green hydrogen in steel, or SOFCs in maritime.
🔍 Spot market shifts, identify rising players, and stay ahead of what's next.

Real-time insights. Precision targeting.

Clear. Fast. Actionable. 🚀`,
    companyReport: `Unpack Any Company’s Playbook in One Click.

Get a structured, data-rich snapshot of how any company operates within a specific sector—<b>investments, partnerships, emerging technologies, product launches, and key market moves</b>—all in one place.

<b>What’s Inside?</b>
🔹 <b>About</b> – How the company positions itself.
🔹 <b>Partnerships</b> – Key alliances shaping its strategy.
🔹 <b>Investments</b> – Where the money’s going.
🔹 <b>Emerging Tech</b> – Future bets and R&D focus.
🔹 <b>Products</b> – Game-changing launches.
🔹 <b>What to Watch</b> – Big moves and market impact.

<b>Why Use It?</b>
🚀 Know any company’s strategy in seconds.
🔍 Track competitors, partners, and opportunities.
📊 Perfect for research, sales, and strategy teams.
`,
    installationAnalysis: `Stay ahead with real-time insights on the <b>largest and most impactful clean tech installations worldwide</b>. From <b>fuel cells to carbon capture and hydrogen projects</b>, get a clear picture of where the industry is scaling.

<b>What’s Inside?</b>
🔹 <b>Largest Installations</b> – Ranked by capacity, impact, and scale.
🔹 <b>Recent Trends</b> – See where the biggest clean tech investments are happening.
🔹 <b>Applications & Use Cases</b> – Understand how these deployments are transforming industries.
🔹 <b>Key Players</b> – Discover the companies leading the charge in clean energy expansion.

<b>Why Use It?</b>
🚀 Spot industry trends and growth hotspots.
🔍 Track major installations shaping the clean energy future.
📊 Perfect for strategy, investment decisions, and competitive analysis.
`,
    cancellationAnalysis: `Stay ahead with real-time insights on <b>major clean tech projects that have been canceled, delayed, or abandoned</b>. Understanding cancellations helps you anticipate market risks, policy shifts, financial struggles, and technological challenges.

<b>What’s Inside?</b>
🔹 <b>Largest Canceled Projects</b> – Ranked by scale, investment, and impact.
🔹 <b>Recent Trends</b> – See why major clean tech deployments are being scrapped.
🔹 <b>Challenges & Barriers</b> – Understand financial, regulatory, and technical hurdles.
🔹 <b>Key Players</b> – Identify which companies faced setbacks and how they are responding.

<b>Why Use It?</b>
🚀 Spot risks and challenges in emerging markets.
🔍 Track major project failures to inform future strategies.
📊 Perfect for investment decisions, risk assessment, and competitive intelligence.
`,
    regulations: `Get instant, sector-specific insights in under <b>400 words</b>-straight from the latest web, blogs, and top news sources.

Any clean tech like carbon capture, fuel cells,

🔹 Always Up to Date – Stay ahead with the most recent market trends, policies, and deals—no outdated reports.
🔹 Laser-Focused Accuracy – Unlike generic AI, Enki zeroes in on energy transition for relevant, precise insights.
🔹 Faster Research, Smarter Strategy – Skip the hours of searching—get real-time answers tailored to your industry.

Clear. Fast. Actionable. 🚀
`,
    "m&a": `Get instant, sector-specific insights in under <b>400 words</b>-straight from the latest web, blogs, and top news sources.

Any clean tech like carbon capture, fuel cells,

🔹 Always Up to Date – Stay ahead with the most recent market trends, policies, and deals—no outdated reports.
🔹 Laser-Focused Accuracy – Unlike generic AI, Enki zeroes in on energy transition for relevant, precise insights.
🔹 Faster Research, Smarter Strategy – Skip the hours of searching—get real-time answers tailored to your industry.

Clear. Fast. Actionable. 🚀
`,
    thesisValidation: `Get instant, sector-specific insights in under <b>400 words</b>-straight from the latest web, blogs, and top news sources.

Any clean tech like carbon capture, fuel cells,

🔹 Always Up to Date – Stay ahead with the most recent market trends, policies, and deals—no outdated reports.
🔹 Laser-Focused Accuracy – Unlike generic AI, Enki zeroes in on energy transition for relevant, precise insights.
🔹 Faster Research, Smarter Strategy – Skip the hours of searching—get real-time answers tailored to your industry.

Clear. Fast. Actionable. 🚀
`,
    briefPoints: `Get instant, sector-specific insights in under <b>400 words</b>-straight from the latest web, blogs, and top news sources.

Any clean tech like carbon capture, fuel cells,

🔹 Always Up to Date – Stay ahead with the most recent market trends, policies, and deals—no outdated reports.
🔹 Laser-Focused Accuracy – Unlike generic AI, Enki zeroes in on energy transition for relevant, precise insights.
🔹 Faster Research, Smarter Strategy – Skip the hours of searching—get real-time answers tailored to your industry.

Clear. Fast. Actionable. 🚀
`,
    swotReport: `Transform any company + technology query into a <b>two-period SWOT analysis</b>. Enki automatically extracts Strengths, Weaknesses, Opportunities, and Threats across two recent periods, highlights what changed, and embeds hyperlinks so you can trace every signal back to source data.`,
    pestlReport: `Generate a <b>two-period PESTL analysis</b> for any company, industry, or strategy. Political, Economic, Social, Technological, and Legal & Regulatory signals are contrasted across timeframes to show how commercial conditions evolved, with hyperlinks for instant source validation.`,
}

export const shortReportDescriptions = {
    researchReport: `Ask about any <b>Company</b>, <b>Technology</b>, or <b>Trend</b> across energy markets. Below are a few sample questions to kick-start your search—feel free to customize and explore further!`,
    shortReport: `Ask about any <b>Company</b>, <b>Technology</b>, or <b>Trend</b> across energy markets. Below are a few sample questions to kick-start your search—feel free to customize and explore further!`,
    newsReport: `Ask about any <b>Company</b>, <b>Technology</b>, or <b>Trend</b> across energy markets. Below are a few sample questions to kick-start your search—feel free to customize and explore further!`,
    companyReport: `Ask about any combination of <b>Energy Company + Energy Technology</b>. Below are just examples to kick-start your search—feel free to customize and explore further!`,
    swotReport: `Ask for any <b>Company + Topic</b> combo and we’ll return a two-period SWOT analysis (Strengths, Weaknesses, Opportunities, Threats) with hyperlinks.`,
    pestlReport: `Ask for any <b>Company, Market, or Strategy</b> and receive a two-period PESTL analysis (Political, Economic, Social, Technological, Legal & Regulatory) with linked evidence.`,
    installationAnalysis: `Ask about any <b>Largest Deployments</b> across the Energy Sector. You can <b>add a number</b> to refine your search.Below are just examples to kick-start your search—feel free to customize and explore further!`,
    cancellationAnalysis: `Ask about any <b>Largest Canceled Deployments</b> across the Energy Sector. You can <b>add a number</b> to refine your search.Below are just examples to kick-start your search—feel free to customize and explore further!`,
    regulations: `Ask about any <b>Company</b>, <b>Technology</b>, or <b>Trend</b> across energy markets. Below are a few sample questions to kick-start your search—feel free to customize and explore further!`,
    "m&a": `Ask about any <b>Company</b>, <b>Technology</b>, or <b>Trend</b> across energy markets. Below are a few sample questions to kick-start your search—feel free to customize and explore further!`,
    thesisValidation: `Ask about any <b>Company</b>, <b>Technology</b>, or <b>Trend</b> across energy markets. Below are a few sample questions to kick-start your search—feel free to customize and explore further!`,
    briefPoints: `Ask about any <b>Company</b>, <b>Technology</b>, or <b>Trend</b> across energy markets. Below are a few sample questions to kick-start your search—feel free to customize and explore further!`,
}

export const inputSuggestions = [
  "<b>Competitive Intelligence & Market Positioning</b>",
  "What are cost reduction strategies for Technology X?",
  "What are estimated system costs for Company X or Technology Y?",
  "What are the differentiation of Technology X or Vendor Y?",
  "What are the financial health of companies working in Sector X or Tech Y?",
  "Which investors are active in Clean tech domain X?",
  "What payback period or NPV hurdle do typical customers require for projects using Technology X?",
  "How many manufacturing lines or GWh/kt capacity does Company X have allocated to Technology Y?",
  "Where are capability gaps (e.g., digital controls, field-service network) that must be filled to win in Sector Z?",
  "How resilient is Company X’s supply chain to critical-material price shocks or export bans?",
  "What is the average CAPEX/OPEX or $/kW cost for Technology X?",
  "Provide the Levelized Cost of Energy (LCOE) comparison of Technology A, Technology B, Technology C.",

  "<b>Forecasts and Market Sizing</b>",
  "What is the market forecast for Technology X in application/sector Y by year Z?",
  "What assumptions are being used in market forecasts for Technology X?",
  "What needs to happen for Technology X to scale in sector Y?",
  "What are the constraints holding back Technology X adoption?",
  "What drivers are accelerating Technology X growth in Market Y?",
  "What are the risks for Technology X?",
  "What is the total addressable market (TAM) for Technology X today, and the forecast CAGR to 2030?",
  "What scenario forecasts (base, upside, downside) exist for Technology X adoption to 2040?",
  "How does price elasticity affect projected demand for Technology X at $X / $Y / $Z unit costs?",

  "<b>Market Share and Competitive Landscape</b>",
  "What is the market share of Company X in specific product or service?",
  "Which companies are gaining or losing ground in market/region X?",
  "How does Company's footprint compare to competitors in region/segment X?",
  "Are there shifts in supplier or customer concentration in Industry Y?",

  "<b>Opportunity Identification</b>",
  "What are the major growth areas in Clean tech sector X over the next 5 years?",
  "Which companies are entering the Sector X recently?",
  "What are the opportunities for the Clean Sector X?",
  "What are the key commercial use cases emerging in sector X?",
  "Are emerging regions (LATAM, Southeast Asia) showing high interest but low supply for Technology X?",
  "What new use cases for Technology X appear in industry articles or conference transcripts from the past 12 months? Use Enki’s date filter.",

  "<b>Opportunity Identification & Product-Market Fit</b>",
  "What markets would benefit most from Technology X?",
  "How good of a fit is Technology X for market Y?",
  "Which markets are willing to pay a premium for product feature X?",
  "What are the priorities for application (e.g., uptime, deployability, efficiency) in industry X?",

  "<b>Policy and Government Funding</b>",
  "What impact will recent US policy changes have on sector X?",
  "How is DAC funding expected to change under Trump vs. Biden?",
  "Which government departments have reduced funding for relevant tech area?",
  "What are the implications of cuts to specific policy for company/project X?",
  "Where is the new government funding for sector being allocated?",

  "<b>Policy and Regulation</b>",
  "What are the global policy updates around hydrogen?",
  "What are the top five countries to target for technology X?",
  "What subsidies are available for hydrogen projects in region?",
  "What are the U.S. regulations around technology X?",
  "Which U.S. states rank highest for renewable energy credits for technology?",
  "Are new U.S. Section 232 tariffs likely on imported X Critical Minerals used in Technology Y?",
  "Do any “24-hour clean-energy” rules force Industry X to add clean-tech capacity?",
  "How does regulation SB 1202 streamline solar-plus-storage permitting?",
  "How does the EU ETS Phase IV price trajectory affect green-hydrogen electrolyser projects in heavy industry?",
  "Does the proposed EU Critical Raw Materials Act classify Critical Minerals X as strategic, and how might quotas affect Technology Y pricing?",
  "When will ISO 19880-1 hydrogen-refuelling standards be updated, and what testing does that add for Technology X?",
  "Which ports risk IMO NOₓ Tier III penalties unless they adopt Technology Y?",
  "When will technology-specific standards (UL, ISO, MIL-STD) be revised that could tighten requirements for Technology X?",
  "What are the U.S. regulations around Technology X (e.g., carbon capture, methane emissions, solar powers)?",

  "<b>Restructuring and Executive Signals</b>",
  "Has Company undergone any major leadership changes recently?",
  "What does recent executive turnover at Company suggest?",
  "Are there layoffs, restructurings, or reorganizations underway in market/region?",
  "Is Company hiring or exiting certain capabilities based on recent announcements?",

  "<b>Technology and Application Fit</b>",
  "What are the pain points or limitations of application X?",
  "What technical constraints do operators face in sector X?",
  "What’s driving decisions between technologies in application?",
  "What are the key priorities or value drivers for application X?",
  "What technical or operational features matter most in application X?",
  "What is the technology readiness level for Technology X?",
  "What total-cost-of-ownership (TCO) thresholds do buyers cite when evaluating Technology X vs. incumbent solutions?",
  "What operational, financial, or compliance pain points are noted in case studies supporting Technology X?",

  "<b>Technology Understanding</b>",
  "What is emerging technology and how is it used in sector?",
  "What are the pros and cons of technology compared to alternative tech?",
  "How does Company's approach to tech differ from competitors?",
  "Which companies or sectors are facing issues (e.g., methane slip, air quality control, carbon emissions) that Technology X mitigates?",
  "Which customer segments (utilities, defence, data-centres) are early adopters of Technology X?",
  "What power-density & current-density ranges exist for each electrolyser type (PEM, AEM, SOEC, alkaline)?",
  "What are the degradation mechanisms of Technology X?",
  "Which KPIs (uptime, emissions cut, $-savings) are reported in completed pilots of Technology X?",
  "What top-3 pain points do Sector X operators cite when discussing Technology X alternatives?",

  "<b>Financial & Investment Intelligence</b>",
  "Which investors are active in Clean tech domain X?",
  "Which investors finance (e.g., waste-to-energy, carbon capture, fuel cells) projects in North America?",
  "What funding rounds have flowed into Company X in the past three years?",
  "What equity investments has GE Aerospace Carbon Solutions disclosed?"
]